import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronRight, Play, Trash2 } from 'lucide-react';
import {
  buildWeekGroups,
  can,
  fmtDec,
  fmtHMS,
  fmtTime,
  groupSameEntries,
  type EntryGroup,
  type Project,
  type Task,
} from '@foodstyles/shared';
import { useNow } from '../../context/NowContext';
import { useAuth } from '../../context/AuthContext';
import { useBootstrap } from '../../hooks/useBootstrap';
import { useToast } from '../../context/ToastContext';
import { useDeleteEntry, useEntries, useStartTimer, useUpdateEntry } from '../../hooks/useEntries';
import { Card, EmptyState, Spinner } from '../../components/primitives';
import { TaskCombobox } from '../../components/TaskCombobox';
import { cn } from '../../lib/cn';
import { ApiError } from '../../lib/api';
import type { EntryDto } from '../../types/api';
import { TimerBar } from './TimerBar';

type MetaPatch = { description?: string; projectId?: string | null; taskId?: string | null };

function EntryRow({
  entry,
  ownEntry,
  canEditMeta,
  projects,
  tasks,
  canContinue,
  onContinue,
  onDelete,
  onSaveMeta,
  indent,
}: {
  entry: EntryDto;
  ownEntry: boolean;
  canEditMeta: boolean;
  projects: Project[];
  tasks: Task[];
  canContinue: boolean;
  onContinue: (e: EntryDto) => void;
  onDelete: (e: EntryDto) => void;
  onSaveMeta: (id: string, meta: MetaPatch, opts?: { onError?: () => void }) => void;
  indent?: boolean; // nested under an expanded group
}) {
  const now = useNow();
  const [taskEditing, setTaskEditing] = useState(false);
  const descRef = useRef<HTMLInputElement>(null);
  const running = entry.stop == null;
  const label = [entry.projectName, entry.taskName].filter(Boolean).join(' · ');
  // The running entry is edited in the TimerBar (single source of truth for its
  // draft); editing it inline here would be silently overwritten on Stop.
  const editable = canEditMeta && !running;

  const saveDescription = () => {
    const el = descRef.current;
    if (!el) return;
    const v = el.value.trim();
    el.value = v; // show the trimmed value that's actually saved
    if (v === entry.description) return;
    onSaveMeta(
      entry.id,
      { description: v },
      { onError: () => { if (descRef.current) descRef.current.value = entry.description; } }, // revert on failure
    );
  };

  return (
    <div
      className={cn(
        'flex items-center gap-3 py-2.5 pr-4 last:rounded-b-xl',
        indent ? 'pl-10' : 'pl-4',
        running && 'bg-[#FF5A3C]/[0.04] dark:bg-[#FF5A3C]/10',
      )}
    >
      <div className="min-w-0 flex-1">
        {/* Description — click to edit inline */}
        {editable ? (
          <input
            ref={descRef}
            key={entry.description}
            defaultValue={entry.description}
            placeholder="(no description)"
            aria-label="Description"
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur();
              else if (e.key === 'Escape') {
                e.currentTarget.value = entry.description;
                e.currentTarget.blur();
              }
            }}
            onBlur={saveDescription}
            className="w-full rounded-md bg-transparent px-1.5 py-0.5 text-sm font-medium text-slate-800 placeholder:text-slate-400 hover:bg-slate-100 focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#FF5A3C]/50 dark:text-slate-100 dark:placeholder:text-slate-500 dark:hover:bg-slate-700 dark:focus:bg-slate-900"
          />
        ) : (
          <div className="truncate px-1.5 py-0.5 text-sm font-medium text-slate-800 dark:text-slate-100">
            {entry.description || '(no description)'}
          </div>
        )}

        {/* Task — click to edit inline (opens the task picker in place) */}
        {editable ? (
          taskEditing ? (
            <div className="mt-0.5">
              <TaskCombobox
                autoFocus
                projects={projects}
                tasks={tasks}
                projectId={entry.projectId}
                taskId={entry.taskId}
                selectedLabel={entry.taskName ? label : undefined}
                onPick={({ projectId, taskId }) => {
                  onSaveMeta(entry.id, { projectId: projectId || null, taskId: taskId || null });
                  setTaskEditing(false);
                }}
                onClose={() => setTaskEditing(false)}
              />
            </div>
          ) : (
            <button
              onClick={() => setTaskEditing(true)}
              className="block w-full truncate rounded-md px-1.5 py-0.5 text-left text-[13px] text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700"
            >
              {label || 'Select a task…'}
            </button>
          )
        ) : (
          <div className="truncate px-1.5 py-0.5 text-[13px] text-slate-500 dark:text-slate-400">{label || '—'}</div>
        )}
      </div>

      <div className="hidden font-mono text-[13px] tabular-nums text-slate-400 sm:block">
        {fmtTime(entry.start)} – {running ? '…' : fmtTime(entry.stop!)}
      </div>
      <div
        className={cn(
          'w-20 text-right font-mono text-sm font-semibold tabular-nums',
          running ? 'text-[#D8341A] dark:text-[#FF8A6B]' : 'text-slate-700 dark:text-slate-200',
        )}
      >
        {running ? fmtHMS(now - entry.start) : fmtDec(entry.stop! - entry.start)}
      </div>
      {!running && (
        <button
          onClick={() => onContinue(entry)}
          disabled={!canContinue}
          aria-label="Continue this entry"
          title={canContinue ? 'Start a new timer with the same details' : 'Stop the running timer first'}
          className="rounded p-1.5 text-slate-400 hover:bg-[#FF5A3C]/10 dark:hover:bg-[#FF5A3C]/20 hover:text-[#D8341A] dark:hover:text-[#FF8A6B] disabled:opacity-30"
        >
          <Play size={15} />
        </button>
      )}
      <button
        onClick={() => onDelete(entry)}
        disabled={!ownEntry}
        aria-label="Delete entry"
        className="rounded p-1.5 text-slate-400 hover:bg-rose-50 dark:hover:bg-rose-500/10 hover:text-rose-600 dark:hover:text-rose-400 disabled:opacity-30"
      >
        <Trash2 size={15} />
      </button>
    </div>
  );
}

// A collapsed group of identical entries (same description + project + task).
// Click the header to expand into the individual, inline-editable rows.
function GroupRow({
  group,
  groupKey,
  isOpen,
  onToggle,
  ownEntryOf,
  canEditMetaOf,
  projects,
  tasks,
  canContinue,
  onContinue,
  onDelete,
  onSaveMeta,
}: {
  group: EntryGroup<EntryDto>;
  groupKey: string;
  isOpen: boolean;
  onToggle: (key: string) => void;
  ownEntryOf: (e: EntryDto) => boolean;
  canEditMetaOf: (e: EntryDto) => boolean;
  projects: Project[];
  tasks: Task[];
  canContinue: boolean;
  onContinue: (e: EntryDto) => void;
  onDelete: (e: EntryDto) => void;
  onSaveMeta: (id: string, meta: MetaPatch, opts?: { onError?: () => void }) => void;
}) {
  const sample = group.sample;
  const label = [sample.projectName, sample.taskName].filter(Boolean).join(' · ');
  return (
    <div className="last:rounded-b-xl">
      <div className="flex items-center gap-3 py-2.5 pl-4 pr-4">
        <button
          onClick={() => onToggle(groupKey)}
          aria-expanded={isOpen}
          aria-label={isOpen ? 'Collapse group' : 'Expand group'}
          className="flex min-w-0 flex-1 items-center gap-2.5 rounded-md py-0.5 text-left hover:bg-slate-50 dark:hover:bg-slate-700"
        >
          <ChevronRight size={14} className={cn('shrink-0 text-slate-400 transition-transform', isOpen && 'rotate-90')} />
          <span className="flex h-5 min-w-[20px] shrink-0 items-center justify-center rounded bg-slate-100 px-1 text-xs font-semibold text-slate-500 dark:bg-slate-700 dark:text-slate-300">
            {group.entries.length}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium text-slate-800 dark:text-slate-100">
              {sample.description || '(no description)'}
            </span>
            <span className="block truncate text-[13px] text-slate-500 dark:text-slate-400">{label || '—'}</span>
          </span>
        </button>
        <div className="hidden font-mono text-[13px] tabular-nums text-slate-400 sm:block">
          {fmtTime(group.start)} – {group.stop != null ? fmtTime(group.stop) : '…'}
        </div>
        <div className="w-20 text-right font-mono text-sm font-semibold tabular-nums text-slate-700 dark:text-slate-200">
          {fmtDec(group.totalMs)}
        </div>
        <button
          onClick={() => onContinue(sample)}
          disabled={!canContinue}
          aria-label="Continue these entries"
          title={canContinue ? 'Start a new timer with the same details' : 'Stop the running timer first'}
          className="rounded p-1.5 text-slate-400 hover:bg-[#FF5A3C]/10 dark:hover:bg-[#FF5A3C]/20 hover:text-[#D8341A] dark:hover:text-[#FF8A6B] disabled:opacity-30"
        >
          <Play size={15} />
        </button>
        {/* invisible delete-button clone so the trailing column aligns with rows that have a delete action */}
        <span className="p-1.5" aria-hidden>
          <span className="block h-[15px] w-[15px]" />
        </span>
      </div>
      {isOpen && (
        <div className="divide-y divide-slate-100 border-t border-slate-100 dark:divide-slate-700 dark:border-slate-700">
          {group.entries.map((e) => (
            <EntryRow
              key={e.id}
              entry={e}
              indent
              ownEntry={ownEntryOf(e)}
              canEditMeta={canEditMetaOf(e)}
              projects={projects}
              tasks={tasks}
              canContinue={canContinue}
              onContinue={onContinue}
              onDelete={onDelete}
              onSaveMeta={onSaveMeta}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function TrackerScreen() {
  const now = useNow();
  const { user } = useAuth();
  const toast = useToast();
  const boot = useBootstrap();
  const entriesQuery = useEntries();
  const del = useDeleteEntry();
  const start = useStartTimer();
  const update = useUpdateEntry();

  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const entries = entriesQuery.data?.entries ?? [];
  const running = entriesQuery.data?.running ?? null;
  const projects = boot.data?.projects ?? [];
  const tasks = boot.data?.tasks ?? [];
  const weeks = useMemo(
    () =>
      buildWeekGroups(entries, now).map((week) => ({
        ...week,
        days: week.days.map((day) => ({ ...day, groups: groupSameEntries(day.entries, now) })),
      })),
    [entries, now],
  );

  // Keep `expanded` from accumulating keys for groups that no longer exist (after
  // edits/deletes) — otherwise the set grows unbounded and a recreated identical
  // group would surprisingly appear pre-expanded.
  const liveGroupKeys = useMemo(() => {
    const s = new Set<string>();
    for (const w of weeks)
      for (const d of w.days) for (const g of d.groups) if (g.entries.length > 1) s.add(`${d.dayKey}::${g.key}`);
    return s;
  }, [weeks]);
  useEffect(() => {
    setExpanded((prev) => {
      let changed = false;
      const next = new Set<string>();
      for (const k of prev) {
        if (liveGroupKeys.has(k)) next.add(k);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [liveGroupKeys]);

  const onError = (e: unknown) => toast(e instanceof ApiError ? e.message : 'Could not save changes.');
  const toggleGroup = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const ownEntryOf = (e: EntryDto) => !!user && can(user, 'deleteEntry', { userId: e.userId });
  const canEditMetaOf = (e: EntryDto) => !!user && can(user, 'editEntryMeta', { userId: e.userId });

  const onDelete = (e: EntryDto) => {
    if (!confirm('Delete this entry?')) return;
    del.mutate(e.id, { onError });
  };

  // "Continue": start a fresh timer carrying the entry's description/project/task.
  const onContinue = (e: EntryDto) => {
    start.mutate(
      { description: e.description, projectId: e.projectId, taskId: e.taskId },
      { onError: (err) => toast(err instanceof ApiError ? err.message : 'Could not start the timer.') },
    );
  };

  const onSaveMeta = (id: string, meta: MetaPatch, opts?: { onError?: () => void }) =>
    update.mutate(
      { id, meta },
      {
        onError: (e) => {
          onError(e);
          opts?.onError?.();
        },
      },
    );

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <TimerBar running={running} recent={entries} />

      {entriesQuery.isLoading ? (
        <div className="flex justify-center py-10">
          <Spinner className="h-7 w-7" />
        </div>
      ) : weeks.length === 0 ? (
        <Card>
          <EmptyState>No entries yet — start a timer above.</EmptyState>
        </Card>
      ) : (
        weeks.map((week) => (
          <section key={week.mondayKey} className="space-y-3">
            <div className="flex items-baseline justify-between px-1">
              <h2 className="text-[15px] font-bold text-slate-800 dark:text-slate-100">{week.label}</h2>
              <span className="text-xs text-slate-400">
                Week total: <span className="font-mono text-sm font-bold text-slate-700 dark:text-slate-200">{fmtDec(week.totalMs)}</span>
              </span>
            </div>
            {week.days.map((day) => (
              <Card key={day.dayKey} pad={false}>
                <div className="flex items-baseline justify-between rounded-t-xl border-b border-slate-100 dark:border-slate-700 bg-slate-50/70 dark:bg-slate-800/60 px-4 py-2">
                  <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">{day.label}</span>
                  <span className="text-[11px] text-slate-400">
                    Total: <span className="font-mono text-[13px] font-semibold text-slate-600 dark:text-slate-300">{fmtDec(day.totalMs)}</span>
                  </span>
                </div>
                <div className="divide-y divide-slate-100 dark:divide-slate-700">
                  {day.groups.map((g) => {
                    const gkey = `${day.dayKey}::${g.key}`;
                    return g.entries.length === 1 ? (
                      <EntryRow
                        key={g.entries[0].id}
                        entry={g.entries[0]}
                        ownEntry={ownEntryOf(g.entries[0])}
                        canEditMeta={canEditMetaOf(g.entries[0])}
                        projects={projects}
                        tasks={tasks}
                        canContinue={!running && !start.isPending}
                        onContinue={onContinue}
                        onDelete={onDelete}
                        onSaveMeta={onSaveMeta}
                      />
                    ) : (
                      <GroupRow
                        key={gkey}
                        group={g}
                        groupKey={gkey}
                        isOpen={expanded.has(gkey)}
                        onToggle={toggleGroup}
                        ownEntryOf={ownEntryOf}
                        canEditMetaOf={canEditMetaOf}
                        projects={projects}
                        tasks={tasks}
                        canContinue={!running && !start.isPending}
                        onContinue={onContinue}
                        onDelete={onDelete}
                        onSaveMeta={onSaveMeta}
                      />
                    );
                  })}
                </div>
              </Card>
            ))}
          </section>
        ))
      )}
    </div>
  );
}
